// Favorites module.
//
// Pattern: Google Maps / Airbnb / Booking bookmark-toggle.
//
// Detail-panel header carries an icon button (`[data-add-favorite]`). When a
// favoritable entity is selected (parcel / building / locality), the button
// is shown and reflects the current saved state via `aria-pressed`.
//
// Click when unpressed  -> opens a comment-only modal (title is server-authored
//                          from the selected entity). Saves POST /rcn/api/favorites.
// Click when pressed    -> confirms, then POST /rcn/api/favorites/toggle removes.
//
// Also renders the user's own favorites as private bookmark pins on the map.
// The `/rcn/api/favorites` endpoint is login-gated + user-scoped, so results
// are naturally private.

import { MarkerGroup, createHtmlMarker } from '../utils/markers';
import { favoriteContextFromToggleAttributes } from '../utils/favorite-context';
import { showToast } from '../utils/toast';

const PENDING_STORAGE_KEY = 'rcn.pendingFavorite';
const PENDING_TTL_MS = 30 * 60 * 1000;
const BOOKMARK_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>';

function escapeHtml(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
}

const REF_TYPES = ['transakcja', 'community_signal', 'property_listing', 'gunb_permit', 'bdot10k_object'];

function describeEntity(ctx) {
    if (!ctx || !ctx.entityType) return '';
    if (ctx.label) return ctx.label;
    if (ctx.entityType === 'dzialka') return 'Działka' + (ctx.entityId ? ' #' + ctx.entityId : '');
    if (ctx.entityType === 'budynek') return 'Budynek' + (ctx.entityId ? ' #' + ctx.entityId : '');
    if (ctx.entityType === 'miejscowosc') return 'Miejscowość' + (ctx.entityId ? ' #' + ctx.entityId : '');
    if (ctx.entityType === 'punkt') return 'Punkt na mapie';
    if (ctx.entityType === 'transakcja') return 'Transakcja' + (ctx.entityRef ? ' ' + ctx.entityRef : '');
    if (ctx.entityType === 'community_signal') return 'Sygnał społeczności' + (ctx.entityRef ? ' #' + ctx.entityRef : '');
    if (ctx.entityType === 'property_listing') return 'Ogłoszenie' + (ctx.entityRef ? ' #' + ctx.entityRef : '');
    if (ctx.entityType === 'gunb_permit') return 'Pozwolenie z Rejestru Głównego Urzędu Nadzoru Budowlanego' + (ctx.entityRef ? ' #' + ctx.entityRef : '');
    if (ctx.entityType === 'bdot10k_object') return 'Obiekt Bazy obiektów topograficznych' + (ctx.entityRef ? ' #' + ctx.entityRef : '');
    return '';
}

function buildModal(entityLabel, initialComment) {
    const overlay = document.createElement('div');
    overlay.className = 'nm-favorite-modal-overlay';
    overlay.innerHTML = ''
        + '<div class="nm-favorite-modal" role="dialog" aria-modal="true" aria-label="Dodaj do ulubionych">'
        + '  <header class="nm-favorite-modal__head">'
        + '    <h3>Dodaj do ulubionych</h3>'
        + '    <button type="button" class="nm-favorite-modal__close" data-fav-close aria-label="Zamknij">×</button>'
        + '  </header>'
        + '  <form class="nm-favorite-modal__body" data-fav-form>'
        + '    <div class="nm-favorite-modal__target">'
        + '      <span class="nm-favorite-modal__target-icon" aria-hidden="true">📌</span>'
        + '      <span class="nm-favorite-modal__target-label" data-fav-target-label>' + escapeHtml(entityLabel) + '</span>'
        + '    </div>'
        + '    <label class="nm-favorite-modal__field">'
        + '      <span>Komentarz (opcjonalnie)</span>'
        + '      <textarea name="comment" rows="4" maxlength="2000" placeholder="Np. do sprawdzenia wiosną">' + escapeHtml(initialComment || '') + '</textarea>'
        + '    </label>'
        + '    <div class="nm-favorite-modal__error" data-fav-error hidden role="alert"></div>'
        + '    <div class="nm-favorite-modal__actions">'
        + '      <button type="button" class="ms-button ms-button--secondary ms-button--block nm-secondary-btn" data-fav-close>Anuluj</button>'
        + '      <button type="submit" class="ms-button ms-button--primary ms-button--block nm-primary-btn" data-fav-submit>Zapisz</button>'
        + '    </div>'
        + '  </form>'
        + '</div>';
    return overlay;
}

function close(overlay) {
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
}

function showError(overlay, msg) {
    const el = overlay.querySelector('[data-fav-error]');
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
}

function showVerifyPrompt(overlay, msg, resendUrl) {
    const el = overlay.querySelector('[data-fav-error]');
    if (!el) return;
    el.hidden = false;
    el.innerHTML = ''
        + '<div class="nm-favorite-modal__msg">' + escapeHtml(msg || '') + '</div>'
        + '<div class="nm-favorite-modal__verify-actions">'
        + '  <button type="button" class="ms-button ms-button--secondary ms-button--block nm-secondary-btn" data-fav-resend>Wyślij link do potwierdzenia e-maila</button>'
        + '  <span class="nm-favorite-modal__verify-status" data-fav-resend-status></span>'
        + '</div>';
    const btn = el.querySelector('[data-fav-resend]');
    const status = el.querySelector('[data-fav-resend-status]');
    if (!btn) return;
    btn.addEventListener('click', async function () {
        btn.disabled = true;
        if (status) status.textContent = 'Wysyłanie…';
        try {
            const res = await fetch(resendUrl, {
                method: 'POST',
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
            });
            const data = await res.json().catch(function () { return {}; });
            if (res.ok) {
                if (status) status.textContent = data.message || 'Wysłano.';
                btn.disabled = true;
                return;
            }
            if (res.status === 429) {
                if (status) status.textContent = data.message || 'Spróbuj później.';
                if (data.retry_after > 0) startResendCountdown(btn, status, Number(data.retry_after));
                return;
            }
            if (status) status.textContent = data.message || 'Nie udało się wysłać.';
            btn.disabled = false;
        } catch (e) {
            if (status) status.textContent = 'Błąd sieci.';
            btn.disabled = false;
        }
    });
}

function startResendCountdown(btn, status, seconds) {
    let remaining = Math.max(1, Math.floor(seconds));
    btn.disabled = true;
    const tick = function () {
        if (remaining <= 0) {
            btn.disabled = false;
            if (status) status.textContent = '';
            return;
        }
        if (status) status.textContent = 'Kolejna próba za ' + remaining + ' s';
        remaining -= 1;
        setTimeout(tick, 1000);
    };
    tick();
}

function readStorage() {
    try { return window.localStorage; } catch (e) { return null; }
}

function stashPending(context, snapshot, draft) {
    const storage = readStorage();
    if (!storage) return;
    try {
        storage.setItem(PENDING_STORAGE_KEY, JSON.stringify({
            context: context || null,
            snapshot: snapshot || {},
            draft: draft || {},
            savedAt: Date.now(),
        }));
    } catch (e) { /* quota / private mode — silently skip */ }
}

function consumePending() {
    const storage = readStorage();
    if (!storage) return null;
    let raw;
    try { raw = storage.getItem(PENDING_STORAGE_KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { storage.removeItem(PENDING_STORAGE_KEY); } catch (e) { /* ignore */ }
    let parsed;
    try { parsed = JSON.parse(raw); } catch (e) { return null; }
    if (!parsed || !parsed.savedAt || Date.now() - parsed.savedAt > PENDING_TTL_MS) return null;
    return parsed;
}

function favoriteUrlState(context, snapshot) {
    const raw = snapshot && snapshot.urlState ? String(snapshot.urlState) : '';
    if (!raw || !context || context.entityType === 'punkt') return raw;
    try {
        const url = new URL(raw, window.location.origin);
        const removable = [];
        url.searchParams.forEach(function (_value, key) {
            if (key.indexOf('search_') === 0) removable.push(key);
        });
        removable.forEach(function (key) { url.searchParams.delete(key); });
        return url.pathname + url.search + url.hash;
    } catch (e) {
        return raw;
    }
}

function showModalSuccess(overlay, title, subtitle) {
    const body = overlay.querySelector('.nm-favorite-modal__body');
    if (!body) return;
    body.innerHTML = ''
        + '<div class="nm-favorite-modal__success" role="status">'
        + '  <div class="nm-favorite-modal__success-icon" aria-hidden="true">'
        + '    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
        + '  </div>'
        + '  <div class="nm-favorite-modal__success-title">' + escapeHtml(title) + '</div>'
        + (subtitle ? '  <div class="nm-favorite-modal__success-sub">' + escapeHtml(subtitle) + '</div>' : '')
        + '</div>';
}

export function createFavoritesModule(options) {
    const opts = options || {};
    const apiUrl = opts.apiUrl || '/rcn/api/favorites';
    const loginUrl = opts.loginUrl || '/konto/logowanie';
    const resendVerificationUrl = opts.resendVerificationUrl || '/konto/potwierdz-email/ponow';
    const captureState = typeof opts.captureState === 'function' ? opts.captureState : function () { return {}; };
    const getMap = typeof opts.getMap === 'function' ? opts.getMap : function () { return null; };

    const favoriteBtn = document.querySelector('[data-add-favorite]');
    let currentContext = null;            // { entityType, entityId }
    let currentFavoriteId = null;          // id of existing favorite for the current context, if any
    let byEntityAbort = null;
    let syncTogglesAbort = null;

    // -------- Private "my favorites" pin layer --------
    let pinGroup = null;
    let pinFetchAbort = null;

    function ensurePinGroup() {
        const map = getMap();
        if (!map) return null;
        if (!pinGroup) {
            pinGroup = new MarkerGroup();
            pinGroup.addTo(map);
        } else if (pinGroup._map !== map) {
            pinGroup.addTo(map);
        }
        return pinGroup;
    }

    async function reloadPinLayer() {
        const map = getMap();
        if (!map) return;
        try {
            if (pinFetchAbort) pinFetchAbort.abort();
            pinFetchAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const res = await fetch(apiUrl, {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                signal: pinFetchAbort ? pinFetchAbort.signal : undefined,
            });
            if (!res.ok) {
                // Not logged in / feature disabled — wipe existing pins.
                if (pinGroup) pinGroup.clear();
                return;
            }
            const data = await res.json().catch(function () { return {}; });
            const items = Array.isArray(data && data.items) ? data.items : [];
            const group = ensurePinGroup();
            if (!group) return;
            group.clear();
            items.forEach(function (item) {
                if (item.lat == null || item.lng == null) return;
                const html = '<span class="nm-fav-pin__mark">' + BOOKMARK_SVG + '</span>';
                const title = item.title || '';
                const marker = createHtmlMarker(
                    [Number(item.lng), Number(item.lat)],
                    html,
                    'nm-fav-pin',
                    { offset: [0, -10] }
                );
                const el = marker.getElement && marker.getElement();
                if (el) el.setAttribute('title', title);
                group.add(marker);
            });
        } catch (e) {
            if (e && e.name === 'AbortError') return;
            // leave previous pins on transient errors
        }
    }

    function clearPinLayer() {
        if (pinGroup) pinGroup.clear();
    }

    function isFavoritable(ctx) {
        if (!ctx || !ctx.entityType || ctx.entityType === 'punkt') return false;
        if (REF_TYPES.indexOf(ctx.entityType) !== -1) {
            return !!(ctx.entityRef || ctx.entityId || ctx.externalId);
        }
        return !!(ctx.entityId || ctx.externalId);
    }

    function entityQuery(ctx) {
        if (!ctx) return '';
        var parts = ['type=' + encodeURIComponent(ctx.entityType)];
        if (REF_TYPES.indexOf(ctx.entityType) !== -1) {
            var ref = ctx.entityRef || ctx.externalId || ctx.entityId;
            if (ref != null) parts.push('id=' + encodeURIComponent(String(ref)));
        } else if (ctx.externalId) {
            parts.push('external_id=' + encodeURIComponent(ctx.externalId));
        } else if (ctx.entityId != null) {
            parts.push('id=' + encodeURIComponent(ctx.entityId));
        }
        return parts.join('&');
    }

    function setToggleButtonPressed(btn, pressed) {
        if (!btn) return;
        btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        // Shared ms-button--secondary toggles take their pressed look from .is-active.
        btn.classList.toggle('is-active', pressed);
        const label = pressed ? 'Usuń z ulubionych' : 'Dodaj do ulubionych';
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
    }

    function favoriteKey(entityType, entityRef) {
        const type = String(entityType || '').trim();
        const ref = entityRef == null ? '' : String(entityRef).trim();
        if (!type || !ref) return '';
        return type + ':' + ref;
    }

    function listItemEntityRef(item) {
        if (!item || typeof item !== 'object') return '';
        if (item.entity_ref != null && String(item.entity_ref).trim() !== '') return String(item.entity_ref).trim();
        if (item.dzialka_id != null && String(item.dzialka_id).trim() !== '') return String(item.dzialka_id).trim();
        if (item.budynek_id != null && String(item.budynek_id).trim() !== '') return String(item.budynek_id).trim();
        if (item.miejscowosc_id != null && String(item.miejscowosc_id).trim() !== '') return String(item.miejscowosc_id).trim();
        return '';
    }

    function applyToggleStateSet(activeKeys) {
        const buttons = document.querySelectorAll('[data-rcn-fav-toggle][data-fav-entity-type][data-fav-entity-ref]');
        buttons.forEach(function (btn) {
            const entityType = btn.getAttribute('data-fav-entity-type') || '';
            const entityRef = btn.getAttribute('data-fav-entity-ref') || '';
            const key = favoriteKey(entityType, entityRef);
            if (!key) return;
            setToggleButtonPressed(btn, activeKeys.has(key));
        });
    }

    function setBtnState(pressed) {
        if (!favoriteBtn) return;
        setToggleButtonPressed(favoriteBtn, pressed);
    }

    function hideBtn() {
        if (!favoriteBtn) return;
        favoriteBtn.hidden = true;
        setBtnState(false);
    }

    function showBtn() {
        if (!favoriteBtn) return;
        favoriteBtn.hidden = false;
    }

    // Detail panels carry their own bookmark button (`.nm-detail-fav-btn`)
    // which is re-rendered with every panel render and therefore needs its
    // pressed state re-applied whenever the favorite status is known.
    function syncDetailToggleButton(pressed) {
        const btn = document.querySelector('.nm-detail-fav-btn[data-rcn-fav-toggle]');
        if (!btn) return;
        setToggleButtonPressed(btn, pressed);
    }

    async function syncRenderedToggles() {
        const buttons = document.querySelectorAll('[data-rcn-fav-toggle][data-fav-entity-type][data-fav-entity-ref]');
        if (!buttons.length) return;
        try {
            if (syncTogglesAbort) syncTogglesAbort.abort();
            syncTogglesAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const res = await fetch(apiUrl, {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                signal: syncTogglesAbort ? syncTogglesAbort.signal : undefined,
            });
            if (res.status === 401) {
                applyToggleStateSet(new Set());
                return;
            }
            if (!res.ok) return;
            const data = await res.json().catch(function () { return {}; });
            const items = Array.isArray(data && data.items) ? data.items : [];
            const activeKeys = new Set();
            items.forEach(function (item) {
                const key = favoriteKey(item && item.entity_type, listItemEntityRef(item));
                if (key) activeKeys.add(key);
            });
            applyToggleStateSet(activeKeys);
        } catch (e) {
            if (e && e.name === 'AbortError') return;
            // keep current DOM state on transient errors
        }
    }

    function renderFavoriteNote(data) {
        syncDetailToggleButton(!!(data && data.exists));
        const slot = document.querySelector('[data-fav-note]');
        if (!slot) return;
        const body = slot.querySelector('[data-fav-note-body]');
        if (!data || !data.exists) {
            slot.hidden = true;
            if (body) body.innerHTML = '';
            return;
        }
        const title = data.title || '';
        const comment = data.comment || '';
        const created = data.created_at || '';
        let html = '';
        if (title) {
            html += '<div class="nm-detail-fav-note__title">' + escapeHtml(title) + '</div>';
        }
        if (comment) {
            html += '<p class="nm-detail-fav-note__comment">' + escapeHtml(comment) + '</p>';
        } else {
            html += '<p class="nm-detail-fav-note__comment nm-detail-fav-note__comment--empty">Brak komentarza.</p>';
        }
        if (created) {
            html += '<div class="nm-detail-fav-note__meta">Dodano ' + escapeHtml(String(created).substring(0, 10)) + '</div>';
        }
        if (body) body.innerHTML = html;
        slot.hidden = false;
    }

    async function refreshPressedState(ctx) {
        if (!isFavoritable(ctx)) {
            currentFavoriteId = null;
            setBtnState(false);
            renderFavoriteNote(null);
            return;
        }
        try {
            if (byEntityAbort) byEntityAbort.abort();
            byEntityAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const res = await fetch(apiUrl + '/by-entity?' + entityQuery(ctx), {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                signal: byEntityAbort ? byEntityAbort.signal : undefined,
            });
            if (res.status === 401) {
                currentFavoriteId = null;
                setBtnState(false);
                renderFavoriteNote(null);
                return;
            }
            if (!res.ok) return;
            const data = await res.json().catch(function () { return {}; });
            currentFavoriteId = data && data.exists ? Number(data.id) : null;
            setBtnState(!!currentFavoriteId);
            renderFavoriteNote(data);
        } catch (e) {
            if (e && e.name === 'AbortError') return;
            // leave as-is on transient errors
        }
    }

    function setContext(ctx) {
        currentContext = ctx || null;
        if (!isFavoritable(currentContext)) {
            hideBtn();
            currentFavoriteId = null;
            renderFavoriteNote(null);
            return;
        }
        showBtn();
        refreshPressedState(currentContext);
    }

    // Re-applies the favorite state (in-panel note + bookmark button) for the
    // current context. Detail panels render asynchronously and replace their
    // DOM, so the favorite slot/button must be re-synced after each render —
    // otherwise the by-entity result races the panel re-render and is lost.
    function reapplyDetailState() {
        if (isFavoritable(currentContext)) {
            refreshPressedState(currentContext);
        } else {
            renderFavoriteNote(null);
        }
    }

    async function openCommentModal(context, resumed) {
        if (!context) return;
        const label = describeEntity(context);
        const snapshot = captureState() || {};
        const initialComment = resumed ? (resumed.draft && resumed.draft.comment) || '' : '';

        const overlay = buildModal(label, initialComment);
        document.body.appendChild(overlay);

        const form = overlay.querySelector('[data-fav-form]');
        const submitBtn = overlay.querySelector('[data-fav-submit]');
        const commentInput = form.querySelector('textarea[name="comment"]');
        if (commentInput) commentInput.focus();

        overlay.addEventListener('click', function (ev) {
            if (ev.target.closest('[data-fav-close]')) close(overlay);
        });
        document.addEventListener('keydown', function onKey(ev) {
            if (ev.key === 'Escape') {
                close(overlay);
                document.removeEventListener('keydown', onKey);
            }
        });

        form.addEventListener('submit', async function (ev) {
            ev.preventDefault();
            submitBtn.disabled = true;
            showError(overlay, '');

            const comment = commentInput ? commentInput.value : '';
            const refPayload = REF_TYPES.indexOf(context.entityType) !== -1
                ? (context.entityRef != null ? String(context.entityRef) : null)
                : null;
            const payload = {
                entity_type: context.entityType,
                entity_id: refPayload != null ? refPayload : (context.entityId != null ? context.entityId : null),
                entity_external_id: context.externalId || null,
                comment: comment || null,
                url_state: favoriteUrlState(context, snapshot),
                lat: snapshot.lat != null ? Number(snapshot.lat) : null,
                lng: snapshot.lng != null ? Number(snapshot.lng) : null,
                zoom: snapshot.zoom != null ? Number(snapshot.zoom) : null,
                preview_meta: snapshot.previewMeta || null,
            };

            try {
                const res = await fetch(apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    credentials: 'same-origin',
                    body: JSON.stringify(payload),
                });

                if (res.status === 401) {
                    if (typeof window.openAuthModal === 'function') {
                        close(overlay);
                        window.openAuthModal('login', {
                            pendingAction: function () {
                                openCommentModal(context, { draft: { comment: comment } });
                            },
                        });
                        return;
                    }
                    stashPending(context, snapshot, { comment });
                    const back = encodeURIComponent(snapshot.urlState || (location.pathname + location.search + location.hash));
                    window.location.href = loginUrl + '?returnUrl=' + back;
                    return;
                }
                if (res.status === 403) {
                    const data = await res.json().catch(function () { return {}; });
                    stashPending(context, snapshot, { comment });
                    const msg = data.error || 'Potwierdź adres e-mail, aby zapisać ulubione.';
                    if (data.code === 'email_not_verified') {
                        showVerifyPrompt(overlay, msg, resendVerificationUrl);
                    } else {
                        showError(overlay, msg);
                    }
                    submitBtn.disabled = false;
                    return;
                }
                if (res.status === 409) {
                    const data = await res.json().catch(function () { return {}; });
                    currentFavoriteId = data && data.existing_id ? Number(data.existing_id) : null;
                    setBtnState(true);
                    syncRenderedToggles();
                    close(overlay);
                    showToast('Ten obiekt masz już w ulubionych.');
                    return;
                }
                if (!res.ok) {
                    const data = await res.json().catch(function () { return {}; });
                    showError(overlay, data.error || 'Nie udało się zapisać.');
                    submitBtn.disabled = false;
                    return;
                }

                const saved = await res.json().catch(function () { return {}; });
                currentFavoriteId = saved && saved.id ? Number(saved.id) : null;
                setBtnState(true);
                if (saved && saved.id) {
                    renderFavoriteNote(Object.assign({ exists: true }, saved));
                }
                showModalSuccess(overlay, 'Dodano do ulubionych', saved && saved.title ? saved.title : '');
                reloadPinLayer();
                syncRenderedToggles();
                setTimeout(function () { close(overlay); }, 1400);
                if (typeof opts.onSuccess === 'function') opts.onSuccess(saved);
            } catch (e) {
                showError(overlay, 'Błąd sieci. Spróbuj ponownie.');
                submitBtn.disabled = false;
            }
        });
    }

    async function removeByToggle(context) {
        if (!context) return;
        const refPayload = REF_TYPES.indexOf(context.entityType) !== -1
            ? (context.entityRef != null ? String(context.entityRef) : null)
            : null;
        try {
            const res = await fetch(apiUrl + '/toggle', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({
                    entity_type: context.entityType,
                    entity_id: refPayload != null ? refPayload : (context.entityId != null ? context.entityId : null),
                    entity_external_id: context.externalId || null,
                }),
            });
            if (res.status === 401) {
                window.location.href = loginUrl + '?returnUrl=' + encodeURIComponent(location.pathname + location.search + location.hash);
                return;
            }
            if (!res.ok) {
                showToast('Nie udało się zaktualizować.');
                return;
            }
            const data = await res.json().catch(function () { return {}; });
            if (data.state === 'removed') {
                currentFavoriteId = null;
                setBtnState(false);
                renderFavoriteNote(null);
                showToast('Usunięto z ulubionych');
                reloadPinLayer();
                syncRenderedToggles();
            } else if (data.state === 'created') {
                currentFavoriteId = data.id ? Number(data.id) : null;
                setBtnState(true);
                renderFavoriteNote(Object.assign({ exists: true }, data));
                showToast('Dodano do ulubionych');
                reloadPinLayer();
                syncRenderedToggles();
            }
        } catch (e) {
            showToast('Błąd sieci.');
        }
    }

    if (favoriteBtn) {
        favoriteBtn.addEventListener('click', function (ev) {
            ev.preventDefault();
            if (!isFavoritable(currentContext)) return;
            const pressed = favoriteBtn.getAttribute('aria-pressed') === 'true';
            if (pressed) {
                if (!window.confirm('Usunąć z ulubionych?')) return;
                removeByToggle(currentContext);
            } else {
                openCommentModal(currentContext, null);
            }
        });
    }

    // Delegated handler for side-panel bookmark buttons. Markup contract:
    //   <button data-rcn-fav-toggle
    //           data-fav-entity-type="transakcja|community_signal|gunb_permit|bdot10k_object"
    //           data-fav-entity-ref="<iip|int>"
    //           data-fav-label="optional human label">
    document.addEventListener('click', function (ev) {
        const btn = ev.target.closest('[data-rcn-fav-toggle]');
        if (!btn) return;
        ev.preventDefault();
        const context = favoriteContextFromToggleAttributes({
            entityType: btn.getAttribute('data-fav-entity-type') || '',
            entityRef: btn.getAttribute('data-fav-entity-ref') || '',
            label: btn.getAttribute('data-fav-label') || '',
        });
        if (!isFavoritable(context)) return;
        const pressed = btn.getAttribute('aria-pressed') === 'true';
        if (pressed) {
            if (!window.confirm('Usunąć z ulubionych?')) return;
            removeByToggle(context).then(function () {
                syncRenderedToggles();
            });
        } else {
            openCommentModal(context, null);
        }
    });

    // Resume after signup / login for the preserved entity context.
    const pending = consumePending();
    if (pending && pending.context) {
        const resumeCtx = pending.context;
        const resume = function () {
            currentContext = resumeCtx;
            openCommentModal(resumeCtx, pending);
        };
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', resume);
        } else {
            resume();
        }
    }

    // Initial pin load once DOM + map are ready. Silently no-ops if not logged in.
    const initialLoad = function () {
        // Give map a moment to finish initial setup.
        setTimeout(reloadPinLayer, 600);
        setTimeout(syncRenderedToggles, 900);
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialLoad);
    } else {
        initialLoad();
    }

    // Public helper used by side-panel renderers (transactions, signals, GUNB
     // permits, BDOT objects). The caller supplies a context object — we look up
     // existing favorite status if needed, then either open the modal or remove.
    async function toggleForContext(context, opts2) {
        if (!isFavoritable(context)) return;
        opts2 = opts2 || {};
        try {
            const res = await fetch(apiUrl + '/by-entity?' + entityQuery(context), {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            if (res.status === 401) {
                stashPending(context, captureState() || {}, {});
                const back = encodeURIComponent(location.pathname + location.search + location.hash);
                window.location.href = loginUrl + '?returnUrl=' + back;
                return;
            }
            const data = res.ok ? await res.json().catch(function () { return {}; }) : {};
            if (data && data.exists) {
                if (opts2.skipConfirm || window.confirm('Usunąć z ulubionych?')) {
                    await removeByToggle(context);
                }
            } else {
                openCommentModal(context, null);
            }
        } catch (e) {
            openCommentModal(context, null);
        }
    }

    // Check favorite status without UI side effects (for bookmark icon rendering).
    async function queryStatus(context) {
        if (!isFavoritable(context)) return { exists: false };
        try {
            const res = await fetch(apiUrl + '/by-entity?' + entityQuery(context), {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            if (!res.ok) return { exists: false };
            return await res.json().catch(function () { return { exists: false }; });
        } catch (e) {
            return { exists: false };
        }
    }

    async function deleteFavorite(id) {
        const res = await fetch(apiUrl + '/' + encodeURIComponent(id), {
            method: 'DELETE',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        });
        if (res.status === 401) {
            window.location.href = loginUrl + '?returnUrl=' + encodeURIComponent(location.pathname + location.search + location.hash);
            return false;
        }
        if (!res.ok && res.status !== 204) return false;
        reloadPinLayer();
        if (currentFavoriteId && Number(currentFavoriteId) === Number(id)) {
            currentFavoriteId = null;
            setBtnState(false);
            renderFavoriteNote(null);
        }
        return true;
    }

    const api = {
        setContext: setContext,
        reapplyDetailState: reapplyDetailState,
        openCommentModal: openCommentModal,
        reloadPinLayer: reloadPinLayer,
        clearPinLayer: clearPinLayer,
        toggleForContext: toggleForContext,
        queryStatus: queryStatus,
        syncRenderedToggles: syncRenderedToggles,
        isFavoritable: isFavoritable,
        listFavorites: async function () {
            const res = await fetch(apiUrl, {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            return { status: res.status, data: res.ok ? await res.json().catch(function () { return {}; }) : {} };
        },
        deleteFavorite: deleteFavorite,
        loginUrl: loginUrl,
    };
    if (typeof opts.onReady === 'function') {
        try { opts.onReady(api); } catch (e) { /* ignore */ }
    }
    return api;
}

// Icon used per entity type in the favorites list. Falls back to bookmark.
const ENTITY_TYPE_ICON = {
    dzialka: 'icon-parcel',
    budynek: 'icon-building',
    miejscowosc: 'icon-loc',
    punkt: 'icon-pin',
    transakcja: 'icon-tran',
    community_signal: 'icon-signal',
    gunb_permit: 'icon-edit',
    bdot10k_object: 'icon-warn',
};

function formatCreatedAt(raw) {
    if (!raw) return '';
    const s = String(raw);
    return s.length >= 10 ? s.substring(0, 10) : s;
}

function renderMapStateAction() {
    return ''
        + '<div class="nm-fav-list__quick-action">'
        + '  <button type="button" class="nm-fav-list__quick-action-btn" data-fav-save-map-state>'
        + '    <svg width="18" height="18" aria-hidden="true" focusable="false"><use href="#icon-pin"/></svg>'
        + '    <span>Zapisz aktualny widok mapy</span>'
        + '  </button>'
        + '</div>';
}

function renderListItems(items) {
    let html = '<div class="nm-fav-list__items">';
    items.forEach(function (item) {
        const iconId = ENTITY_TYPE_ICON[item.entity_type] || 'icon-bookmark';
        const title = item.title || '';
        const comment = item.comment || '';
        const created = formatCreatedAt(item.created_at);
        html += '<button type="button" class="nm-fav-list__item" data-fav-item-id="' + escapeHtml(String(item.id)) + '">'
            + '  <span class="nm-fav-list__item-icon" aria-hidden="true">'
            + '    <svg width="18" height="18" focusable="false"><use href="#' + escapeHtml(iconId) + '"/></svg>'
            + '  </span>'
            + '  <span class="nm-fav-list__item-main">'
            + '    <span class="nm-fav-list__item-title">' + escapeHtml(title) + '</span>'
            + (comment ? '    <span class="nm-fav-list__item-comment">' + escapeHtml(comment) + '</span>' : '')
            + (created ? '    <span class="nm-fav-list__item-meta">' + escapeHtml(created) + '</span>' : '')
            + '  </span>'
            + '  <span class="nm-fav-list__item-remove" data-fav-item-remove aria-label="Usuń" title="Usuń z ulubionych" role="button" tabindex="0">'
            + '    <svg width="16" height="16" aria-hidden="true" focusable="false"><use href="#icon-trash"/></svg>'
            + '  </span>'
            + '</button>';
    });
    html += '</div>';
    return html;
}

function renderEmptyState() {
    return ''
        + '<div class="nm-fav-list__state">'
        + '  <div class="nm-fav-list__state-title">Nie masz jeszcze ulubionych</div>'
        + '  <p class="nm-fav-list__state-text">Otwórz dowolny obiekt na mapie (działkę, budynek, transakcję) i kliknij ikonę zakładki, aby zapisać go tutaj.</p>'
        + '  <button type="button" class="nm-fav-list__state-action" data-fav-save-map-state>Zapisz aktualny widok mapy</button>'
        + '</div>';
}

function renderLoadingState() {
    return ''
        + '<div class="nm-fav-list__state">'
        + '  <span class="nm-fav-list__spinner" aria-hidden="true"></span>'
        + '  <div class="nm-fav-list__state-text">Wczytywanie…</div>'
        + '</div>';
}

function renderUnauthState(loginUrl) {
    const back = encodeURIComponent(location.pathname + location.search + location.hash);
    return ''
        + '<div class="nm-fav-list__state">'
        + '  <div class="nm-fav-list__state-title">Zaloguj się, aby zobaczyć ulubione</div>'
        + '  <p class="nm-fav-list__state-text">Ulubione są przypisane do konta. Po zalogowaniu zobaczysz je tutaj.</p>'
        + '  <a class="nm-fav-list__state-action" href="' + escapeHtml(loginUrl) + '?returnUrl=' + back + '">Zaloguj się</a>'
        + '</div>';
}

function renderErrorState() {
    return ''
        + '<div class="nm-fav-list__state">'
        + '  <div class="nm-fav-list__state-title">Coś poszło nie tak</div>'
        + '  <p class="nm-fav-list__state-text">Nie udało się wczytać listy. Spróbuj ponownie za chwilę.</p>'
        + '</div>';
}

// Mounts the favorites-list modal (bottom-sheet on mobile, right panel on
// desktop). Reuses the controller produced by createFavoritesModule for the
// network calls so pin layer stays in sync after deletions.
export function mountFavoritesList(options) {
    const opts = options || {};
    const listEl = opts.listEl;
    const bodyEl = opts.bodyEl;
    const backdropEl = opts.backdropEl;
    const triggerEl = opts.triggerEl;
    const controller = opts.controller;
    if (!listEl || !bodyEl || !triggerEl || !controller) return null;
    const loginUrl = controller.loginUrl || '/konto/logowanie';

    let isOpen = false;
    let lastFocus = null;
    let currentItems = [];

    function setExpanded(expanded) {
        triggerEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    function setListShown(shown) {
        if (shown) {
            listEl.hidden = false;
            if (backdropEl) backdropEl.hidden = false;
            // Force reflow so the transform transition runs.
            void listEl.offsetWidth;
            listEl.classList.add('is-open');
            listEl.setAttribute('aria-hidden', 'false');
            if (backdropEl) backdropEl.classList.add('is-open');
        } else {
            listEl.classList.remove('is-open');
            listEl.setAttribute('aria-hidden', 'true');
            if (backdropEl) backdropEl.classList.remove('is-open');
            // Hide after transition completes (covers reduced-motion too).
            setTimeout(function () {
                if (!listEl.classList.contains('is-open')) {
                    listEl.hidden = true;
                    if (backdropEl) backdropEl.hidden = true;
                }
            }, 320);
        }
    }

    async function refresh() {
        bodyEl.innerHTML = renderLoadingState();
        try {
            const result = await controller.listFavorites();
            if (result.status === 401 || result.status === 403) {
                bodyEl.innerHTML = renderUnauthState(loginUrl);
                return;
            }
            if (!(result.status >= 200 && result.status < 300)) {
                bodyEl.innerHTML = renderErrorState();
                return;
            }
            const items = Array.isArray(result.data && result.data.items) ? result.data.items : [];
            currentItems = items;
            if (!items.length) {
                bodyEl.innerHTML = renderEmptyState();
                return;
            }
            bodyEl.innerHTML = renderMapStateAction() + renderListItems(items);
        } catch (e) {
            currentItems = [];
            bodyEl.innerHTML = renderErrorState();
        }
    }

    function open() {
        if (isOpen) return;
        isOpen = true;
        lastFocus = document.activeElement;
        setExpanded(true);
        setListShown(true);
        refresh();
        const closeBtn = listEl.querySelector('[data-fav-list-close]');
        if (closeBtn) {
            try { closeBtn.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        }
    }

    function close() {
        if (!isOpen) return;
        isOpen = false;
        setExpanded(false);
        setListShown(false);
        if (lastFocus && typeof lastFocus.focus === 'function') {
            try { lastFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        }
    }

    function navigateToItem(itemId) {
        const match = currentItems.find(function (it) { return String(it.id) === String(itemId); });
        if (match && match.url_state) {
            window.location.href = favoriteUrlState({ entityType: match.entity_type }, { urlState: match.url_state });
            return;
        }
        close();
    }

    triggerEl.addEventListener('click', function () {
        if (isOpen) close(); else open();
    });

    listEl.addEventListener('click', function (ev) {
        if (ev.target.closest('[data-fav-list-close]')) {
            ev.preventDefault();
            close();
            return;
        }
        if (ev.target.closest('[data-fav-save-map-state]')) {
            ev.preventDefault();
            close();
            controller.openCommentModal({ entityType: 'punkt', entityId: null, label: 'Aktualny widok mapy' }, null);
            return;
        }
        const removeBtn = ev.target.closest('[data-fav-item-remove]');
        if (removeBtn) {
            ev.preventDefault();
            ev.stopPropagation();
            const item = removeBtn.closest('[data-fav-item-id]');
            const id = item ? item.getAttribute('data-fav-item-id') : null;
            if (!id) return;
            if (!window.confirm('Usunąć z ulubionych?')) return;
            controller.deleteFavorite(id).then(function (ok) {
                if (ok) refresh();
            });
            return;
        }
        const itemBtn = ev.target.closest('[data-fav-item-id]');
        if (itemBtn) {
            ev.preventDefault();
            const id = itemBtn.getAttribute('data-fav-item-id');
            navigateToItem(id);
        }
    });

    if (backdropEl) {
        backdropEl.addEventListener('click', function () { close(); });
    }

    document.addEventListener('keydown', function (ev) {
        if (ev.key === 'Escape' && isOpen) close();
    });

    return { open: open, close: close, refresh: refresh };
}
